import { navigateToUrl } from '../../common'
import {
  getFromSessionStorage,
  getLocationHREF,
  getLocationObject,
  getLocationPathname,
  removeFromSessionStorage,
  setToSessionStorage,
} from '../../common/dom-helpers'
import { ROOT_ROUTE, SEARCH_ROUTE } from '../../common/routes'
import { getShellApiInstance } from '../../common/shell-api-helpers/shell-api-instance'
import { SessionStorageKeys } from '../../environments'
import { HttpResponseError } from '../../http-utils'
import { executeCommand, type CommandDefinition } from '../../services/namespaces'
import type {
  GlobalSearchApiError,
  GlobalSearchResultItem,
  GlobalSearchResultItemJSON,
  GlobalSearchQueryUI,
  GlobalSearchResponse,
  GlobalSearchResponseJSON,
  GlobalSearchResultUI,
} from './global-search-models'

const EMPTY_OPTIMIZED_QUERY = { q: '' }
const getUserPreferences = () => getShellApiInstance().userPreferences

export const getSearchHistory = async (): Promise<readonly GlobalSearchQueryUI[]> => {
  const history =
    (await getUserPreferences()?.getPreference<string[], OptimizedSearchQueryUI[]>('search-history', [])) ?? []
  return history.reduce((queries: GlobalSearchQueryUI[], optimizedQuery: OptimizedSearchQueryUI) => {
    const query = convertToGlobalSearchQueryUI(optimizedQuery)
    if (query) {
      queries.push(query)
    }
    return queries
  }, [])
}

interface OptimizedSearchQueryUI {
  readonly q: string
}

const convertToOptimizedSearchQueryUI = (searchQuery: GlobalSearchQueryUI): OptimizedSearchQueryUI => ({
  q: searchQuery.query,
})

const convertToGlobalSearchQueryUI = (searchQuery: OptimizedSearchQueryUI): GlobalSearchQueryUI => ({
  query: searchQuery.q,
})

export const searchQueryToBase64 = (searchQuery: GlobalSearchQueryUI): string => {
  const optimized = convertToOptimizedSearchQueryUI(searchQuery)
  const str = JSON.stringify(optimized)
  return btoa(str)
}

export const base64ToSearchQuery = (base64: string): GlobalSearchQueryUI | undefined => {
  try {
    const str = atob(base64)
    const query = JSON.parse(str)
    return convertToGlobalSearchQueryUI(query)
  } catch (e) {
    return undefined
  }
}

export const getCurrentSearchQuery = (): GlobalSearchQueryUI =>
  convertToGlobalSearchQueryUI(getLocationObject<OptimizedSearchQueryUI>('search') ?? EMPTY_OPTIMIZED_QUERY)

const removeFromSearchHistoryArray = (
  searchQuery: GlobalSearchQueryUI,
  // eslint-disable-next-line functional/prefer-readonly-type
  searchHistory: GlobalSearchQueryUI[],
) => {
  const index = searchHistory.findIndex(query => searchQueryToBase64(searchQuery) === searchQueryToBase64(query))
  if (index >= 0) {
    searchHistory.splice(index, 1)
  }
}

export const addToSearchHistory = async (searchQuery: GlobalSearchQueryUI) => {
  const userPreferences = getUserPreferences()
  const searchHistory = [...(await getSearchHistory())]
  removeFromSearchHistoryArray(searchQuery, searchHistory)
  searchHistory.unshift(searchQuery)
  userPreferences?.setPreference(
    'search-history',
    searchHistory.map(query => convertToOptimizedSearchQueryUI(query)),
  )
}

export const removeFromSearchHistory = async (searchQuery: GlobalSearchQueryUI) => {
  const userPreferences = getUserPreferences()
  const searchHistory = [...(await getSearchHistory())]
  removeFromSearchHistoryArray(searchQuery, searchHistory)
  userPreferences?.setPreference(
    'search-history',
    searchHistory.map(query => convertToOptimizedSearchQueryUI(query)),
  )
}

export const navigateToSearchPage = (searchQuery: GlobalSearchQueryUI) => {
  if (getLocationHREF().indexOf(SEARCH_ROUTE) === -1) {
    setToSessionStorage(SessionStorageKeys.pathBeforeNavigationToSearchPage, getLocationPathname())
  }
  navigateToUrl(`${SEARCH_ROUTE}/${searchQueryToBase64(searchQuery)}`)
}

export const closeSearchPage = () => {
  const pathBeforeNavigationToSearchPage = getFromSessionStorage(SessionStorageKeys.pathBeforeNavigationToSearchPage)
  if (pathBeforeNavigationToSearchPage) {
    removeFromSessionStorage(SessionStorageKeys.pathBeforeNavigationToSearchPage)
    navigateToUrl(pathBeforeNavigationToSearchPage)
  } else {
    navigateToUrl(ROOT_ROUTE)
  }
}

/**
 * Loops over the response items and converts the data string into an object
 * @param response - the raw response from the global search API
 * @returns response with items array containing data as a GlobalSearchResultItem
 */
export const parseResultData = (response: GlobalSearchResponseJSON | undefined): GlobalSearchResponse | undefined => {
  if (response) {
    return {
      ...response,
      items: response.items.map((item: GlobalSearchResultItemJSON) => ({
        ...item,
        data: JSON.parse(item.data),
      })) as GlobalSearchResultItem[],
    } as GlobalSearchResponse
  }
  return response
}

/**
 * Logs the error received by the API if the response is not ok
 * @param response API Response
 * @returns Returns the HTTP Response if the response is ok
 */
export const handleApiResponse = async (response: Response | undefined): Promise<Response | undefined> => {
  if (response?.ok) {
    return response
  } else {
    const { status = 412, statusText = 'Precondition Failed', body = null } = response ?? {}
    const responseJson = (await response?.json()) as GlobalSearchApiError
    const message = responseJson?.message ?? 'Precondition Failed'
    throw new HttpResponseError({ message, response, status, statusText, body })
  }
}

export const executeSearchResultDefaultActionCommand = (searchResult: GlobalSearchResultUI, searchString: string) => {
  if (searchResult.defaultAction?.command) {
    executeGlobalSearchCommand(searchResult.defaultAction.command, searchString, searchResult.originalResult.data)
  }
}

/**
 * Execute a given command for global search context
 * @param command Command to execute
 * @param searchString searched string (or query)
 * @param resultItem search result item
 */
export const executeGlobalSearchCommand = (
  command: CommandDefinition,
  searchString: string,
  resultItemData?: unknown,
) => {
  executeCommand(command, searchString, resultItemData)
}
